// External Dependencies
import { FC, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { AxiosError } from 'axios';

// Internal Dependencies
import useTextField from 'hooks/useTextField';
import { EnhancedAlert, SaveButton } from 'components/shared';
import { useReviewPurchaseOrder } from 'hooks/api/purchaseOrders';
import { convertAxiosErrorToYupError } from 'utils/convertAxiosErrorToYupError';

// Local Typings
interface Props {
  estimateId: string;
  isOpen: boolean;
  onClose: () => void;
  purchaseOrderId: string;
}

// Component Definition
const RejectPurchaseOrderDialog: FC<Props> = ({
  estimateId,
  isOpen,
  onClose,
  purchaseOrderId,
}) => {
  const rejectionReasonField = useTextField();

  const {
    error,
    isLoading: isSubmitting,
    mutate: reviewPurchaseOrder,
  } = useReviewPurchaseOrder(estimateId);

  const handleClickConfirm = useCallback(() => {
    reviewPurchaseOrder({
      body: {
        isApproved: false,
        purchaseOrderId,
        rejectedReason: rejectionReasonField.value,
      },
      params: { purchaseOrderId },
    }, {
      onSuccess: onClose,
    });
  }, [onClose, reviewPurchaseOrder, purchaseOrderId, rejectionReasonField.value]);

  const yupErrors = useMemo(() => {
    if (!error) {
      return null;
    }

    const yupError = convertAxiosErrorToYupError(error as AxiosError);

    if (yupError) {
      return Object.values(yupError);
    }

    return null;
  }, [error]);

  return (
    <Dialog
      onClose={onClose}
      open={isOpen}
    >
      <DialogTitle>Reject Purchase Order?</DialogTitle>

      <DialogContent>
        <DialogContentText gutterBottom>
          Are you sure you want to reject this purchase order?
          Please provide a reason for rejection.
        </DialogContentText>

        <Box marginY={2}>
          <TextField
            {...rejectionReasonField}
            fullWidth
            label="Rejection Reason"
            minRows={4}
            multiline
            required
          />
        </Box>

        {yupErrors?.map((errorMessage) => (
          <EnhancedAlert
            key={errorMessage as string}
            severity="error"
            sx={{ mt: 2 }}
          >
            {errorMessage}
          </EnhancedAlert>
        ))}
      </DialogContent>

      <DialogActions>
        <Button
          disabled={isSubmitting}
          onClick={onClose}
        >
          Cancel
        </Button>

        <SaveButton
          disabled={!rejectionReasonField.value}
          isSaving={isSubmitting}
          onClick={handleClickConfirm}
          variant="contained"
        >
          Reject
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

export default RejectPurchaseOrderDialog;
